import React from 'react';
import {
  Avatar,
  Box,
  Button,
  LinearProgress,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import TouchTooltip from '../../common/TouchTooltip';
import { fmtInt } from '../../../utils/buyerInsightsFormat';

const share = (r) => `${r.share_pct.toLocaleString('en-GB', { maximumFractionDigits: 1 })}% of partner clicks`;

/**
 * Brands you don't carry (spec §1.5): rank · brand · categories · partner clicks (with the share of
 * all partner clicks — relative, never a raw headline) · partners carrying.
 */
const NotCarriedTable = ({ rows, isWide, loading, loadingMore, hasMore, onShowMore, emptyState }) => {
  if (loading && rows.length === 0) {
    return [0, 1, 2].map((i) => <Skeleton key={i} variant="rectangular" height={48} sx={{ borderRadius: 1, mb: 1 }} />);
  }
  if (!loading && rows.length === 0) return emptyState;
  const more = hasMore && (
    <Box sx={{ textAlign: 'center', mt: 2 }}>
      <Button variant="outlined" onClick={onShowMore} disabled={loadingMore} sx={{ minHeight: isWide ? 36 : 44 }}>
        {loadingMore ? 'Loading…' : 'Show more'}
      </Button>
    </Box>
  );
  const avatar = (b) => (
    <Avatar sx={{ width: 28, height: 28, fontSize: 13, bgcolor: '#eceff1', color: 'text.primary' }}>{b.charAt(0).toUpperCase()}</Avatar>
  );
  return (
    <Box sx={{ opacity: loading ? 0.6 : 1 }}>
      {loading && <LinearProgress sx={{ mb: 1 }} />}
      {isWide ? (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small" aria-label="Brands you don't carry">
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 40 }}>#</TableCell>
                <TableCell>Brand</TableCell>
                <TableCell>Categories</TableCell>
                <TableCell align="right">
                  <TouchTooltip title="Clicks your partners received for this brand in this period">
                    <span>Partner clicks</span>
                  </TouchTooltip>
                </TableCell>
                <TableCell align="right">Partners carrying</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.brand} hover>
                  <TableCell>{r.rank}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                      {avatar(r.brand)}
                      {r.brand}
                    </Box>
                  </TableCell>
                  <TableCell sx={{ color: 'text.secondary' }}>{r.categories_text}</TableCell>
                  <TableCell align="right">
                    {fmtInt(r.partner_clicks)}
                    <Typography variant="caption" color="text.secondary" component="div">
                      {share(r)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">{fmtInt(r.partners_carrying)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        rows.map((r) => (
          <Paper key={r.brand} variant="outlined" sx={{ p: 1.5, mb: 1, display: 'flex', gap: 1.5 }}>
            <Typography variant="body2" color="text.secondary" sx={{ width: 20 }}>
              {r.rank}
            </Typography>
            {avatar(r.brand)}
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {r.brand}
              </Typography>
              <Typography variant="caption" color="text.secondary" component="div">
                {r.categories_text}
              </Typography>
              <Typography variant="caption" color="text.secondary" component="div">
                {`${fmtInt(r.partner_clicks)} partner clicks (${share(r)}) · ${fmtInt(r.partners_carrying)} partners carrying`}
              </Typography>
            </Box>
          </Paper>
        ))
      )}
      {more}
    </Box>
  );
};

export default NotCarriedTable;
